/* eslint-disable no-barrel-files/no-barrel-files -- Compatibility layer that re-exports UIKit Typography sub-components */

import { Typography as CompassTypography } from "@circleco/compass/components/Typography";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";

import { TitleLg as UIKitTitleLg } from "@/react/components/shared/uikit/Typography/components/Title/TitleLg";
import type { TitleLgProps } from "@/react/components/shared/uikit/Typography/components/Title/TitleLg";
import { TitleMd as UIKitTitleMd } from "@/react/components/shared/uikit/Typography/components/Title/TitleMd";
import type { TitleMdProps } from "@/react/components/shared/uikit/Typography/components/Title/TitleMd";
import { TitleSm as UIKitTitleSm } from "@/react/components/shared/uikit/Typography/components/Title/TitleSm";
import type { TitleSmProps } from "@/react/components/shared/uikit/Typography/components/Title/TitleSm";
import { TitleXl as UIKitTitleXl } from "@/react/components/shared/uikit/Typography/components/Title/TitleXl";
import type { TitleXlProps } from "@/react/components/shared/uikit/Typography/components/Title/TitleXl";
import { TitleXxl as UIKitTitleXxl } from "@/react/components/shared/uikit/Typography/components/Title/TitleXxl";
import type { TitleXxlProps } from "@/react/components/shared/uikit/Typography/components/Title/TitleXxl";

import { mapColor, extractExtraClasses } from "./colorMap";
import { useTypographyFlag, stripUIKitProps } from "./shared";
import type { TypographyElement } from "./shared";

export type {
  TitleXxlProps,
  TitleXlProps,
  TitleLgProps,
  TitleMdProps,
  TitleSmProps,
};

export const TitleXxl = ({
  children,
  color,
  truncate,
  leading,
  as = "h1",
  "data-testid": dataTestId,
  ...rest
}: TitleXxlProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitTitleXxl
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitTitleXxl>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="heading-3xl"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "h1"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const TitleXl = ({
  children,
  color,
  truncate,
  leading,
  as = "h2",
  "data-testid": dataTestId,
  ...rest
}: TitleXlProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitTitleXl
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitTitleXl>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="heading-2xl"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "h2"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const TitleLg = ({
  children,
  color,
  truncate,
  leading,
  as = "h3",
  "data-testid": dataTestId,
  ...rest
}: TitleLgProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitTitleLg
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitTitleLg>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="heading-xl"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "h3"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const TitleMd = ({
  children,
  color,
  truncate,
  leading,
  as = "h4",
  "data-testid": dataTestId,
  ...rest
}: TitleMdProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitTitleMd
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitTitleMd>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="heading-lg"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "h4"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const TitleSm = ({
  children,
  color,
  truncate,
  leading,
  as = "h5",
  "data-testid": dataTestId,
  ...rest
}: TitleSmProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitTitleSm
        color={color}
        truncate={truncate}
        leading={leading}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitTitleSm>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="heading-md"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "h5"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        extractExtraClasses(color) ?? "",
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};
